export const financialStatementAnalysisCourse = {
  id: "financial-statement-analysis-2024",
  title: "Financial Statement Analysis: Complete Professional Course",
  shortDescription: "Master advanced financial statement analysis, ratio analysis, and credit assessment techniques used by professional analysts and investors.",
  description: "Comprehensive course covering financial statement analysis, ratio analysis, cash flow analysis, and credit assessment techniques essential for investment analysis and corporate finance.",
  category: "Finance",
  industryCategory: "Financial Services",
  difficulty: "intermediate",
  duration: 19,
  estimatedWeeks: 10,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from financial analysts and credit professionals",
  thumbnailUrl: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Analyze financial statements for investment and credit decisions",
    "Calculate and interpret comprehensive financial ratios",
    "Evaluate corporate financial health and performance",
    "Assess working capital management and efficiency",
    "Analyze cash flow patterns and quality of earnings",
    "Compare companies using standardized analysis frameworks",
    "Identify red flags and potential financial problems",
    "Build financial models for forecasting and valuation"
  ],

  prerequisites: [
    "Basic Accounting Principles",
    "Financial Markets Knowledge", 
    "Excel/Spreadsheet Skills"
  ],

  skills: [
    "Financial Analysis",
    "Ratio Analysis",
    "Credit Assessment",
    "Financial Modeling",
    "Industry Comparison",
    "Risk Assessment",
    "Performance Evaluation",
    "Forecasting"
  ],

  tags: [
    "Financial Analysis",
    "Credit Analysis",
    "Financial Ratios",
    "Investment Analysis",
    "Corporate Finance",
    "Financial Modeling",
    "Risk Assessment",
    "Performance Analysis"
  ],

  marketData: {
    averageSalary: 95000,
    salaryRange: {
      min: 65000,
      max: 160000
    },
    jobDemand: 4.3,
    marketTrend: "stable",
    careerPaths: [
      "Financial Analyst",
      "Credit Analyst",
      "Investment Analyst",
      "Equity Research Analyst", 
      "Corporate Finance Analyst",
      "Rating Analyst"
    ],
    relatedSkills: [
      "Excel/VBA",
      "Financial Modeling",
      "Industry Analysis",
      "Valuation",
      "Accounting",
      "Capital Markets"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "statement-fundamentals",
      title: "Financial Statement Fundamentals",
      description: "Understanding financial statements, accounting principles, and statement relationships",
      order: 1,
      estimatedHours: 4.5,
      content: [
        {
          id: "three-statements",
          title: "The Three Financial Statements",
          description: "Income statement, balance sheet, and cash flow statement analysis",
          type: "video",
          duration: 35
        },
        {
          id: "accounting-quality",
          title: "Accounting Quality and Adjustments",
          description: "Identifying accounting distortions and making analytical adjustments",
          type: "finance-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "accounting-adjustments",
            instructions: "Analyze financial statements and make necessary adjustments for better analytical insight. Identify potential red flags and accounting quality issues.",
            scenario: {
              companyData: {
                revenue: 1000000,
                operatingIncome: 150000,
                netIncome: 100000,
                totalAssets: 800000,
                totalLiabilities: 400000,
                cashFlow: 120000
              },
              adjustmentOptions: [
                "One-time charges removal",
                "Operating lease capitalization",
                "Pension obligation adjustment",
                "Inventory valuation method",
                "Depreciation method standardization"
              ]
            },
            hints: [
              "Look for one-time or non-recurring items",
              "Consider off-balance sheet items",
              "Check for aggressive accounting practices",
              "Compare accounting policies across companies"
            ]
          }
        }
      ],
      quiz: {
        id: "fundamentals-quiz",
        title: "Financial Statement Fundamentals Quiz",
        description: "Test your understanding of financial statement basics",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which statement provides information about cash generation and usage?",
            options: [
              "Income Statement",
              "Balance Sheet",
              "Cash Flow Statement",
              "Statement of Equity"
            ],
            correctAnswer: 2,
            explanation: "The Cash Flow Statement shows actual cash inflows and outflows from operating, investing, and financing activities."
          }
        ]
      }
    },

    {
      id: "ratio-analysis",
      title: "Comprehensive Ratio Analysis",
      description: "Profitability, liquidity, leverage, and efficiency ratios with industry comparisons",
      order: 2,
      estimatedHours: 5.0,
      content: [
        {
          id: "profitability-ratios",
          title: "Profitability Analysis",
          description: "ROE, ROA, margins, and profitability trend analysis",
          type: "video",
          duration: 40
        },
        {
          id: "comprehensive-ratio-tool",
          title: "Comprehensive Ratio Analysis Tool",
          description: "Calculate and interpret all major financial ratios with industry benchmarks",
          type: "finance-interactive",
          duration: 60,
          interactiveData: {
            exerciseType: "ratio-analysis",
            instructions: "Perform comprehensive ratio analysis for multiple companies and compare performance across different metrics and time periods.",
            scenario: {
              companies: [
                {
                  name: "Company A",
                  financials: {
                    revenue: 5000000,
                    grossProfit: 2000000,
                    operatingIncome: 800000,
                    netIncome: 600000,
                    totalAssets: 4000000,
                    currentAssets: 1500000,
                    currentLiabilities: 800000,
                    totalDebt: 1200000,
                    equity: 2000000
                  }
                },
                {
                  name: "Company B", 
                  financials: {
                    revenue: 3000000,
                    grossProfit: 1500000,
                    operatingIncome: 600000,
                    netIncome: 450000,
                    totalAssets: 2500000,
                    currentAssets: 1000000,
                    currentLiabilities: 500000,
                    totalDebt: 800000,
                    equity: 1200000
                  }
                }
              ],
              industryAverages: {
                grossMargin: 0.35,
                operatingMargin: 0.15,
                netMargin: 0.10,
                roe: 0.15,
                roa: 0.08,
                currentRatio: 2.0,
                debtToEquity: 0.6
              }
            },
            hints: [
              "Calculate ratios for each company and compare to industry averages",
              "Look for trends over time, not just point-in-time ratios",
              "Consider the business model when interpreting ratios",
              "Identify relative strengths and weaknesses"
            ]
          }
        }
      ],
      quiz: {
        id: "ratio-analysis-quiz",
        title: "Ratio Analysis Quiz",
        description: "Test your understanding of financial ratios and interpretation",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What does a decreasing current ratio over time typically indicate?",
            options: [
              "Improving liquidity position",
              "Deteriorating liquidity position", 
              "More efficient working capital management",
              "Higher profitability"
            ],
            correctAnswer: 1,
            explanation: "A decreasing current ratio over time typically indicates a deteriorating liquidity position, as the company has relatively fewer current assets to cover current liabilities."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which ratio is most useful for comparing companies across different industries?",
            options: [
              "Inventory turnover",
              "Return on equity",
              "Asset turnover",
              "Times interest earned"
            ],
            correctAnswer: 1,
            explanation: "Return on equity is most useful for cross-industry comparison as it measures how efficiently a company generates profits from shareholders' equity, which is relevant across all industries."
          }
        ]
      }
    },

    {
      id: "cash-flow-analysis",
      title: "Cash Flow Analysis",
      description: "Operating cash flow, free cash flow, and cash flow quality assessment",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "operating-cash-flow",
          title: "Operating Cash Flow Analysis",
          description: "Quality of earnings and cash conversion analysis",
          type: "video",
          duration: 35
        },
        {
          id: "cash-flow-comprehensive",
          title: "Advanced Cash Flow Analysis",
          description: "Free cash flow, cash quality, and cash flow forecasting techniques",
          type: "text",
          duration: 50,
          textContent: `# Advanced Cash Flow Analysis

## Operating Cash Flow Analysis

### Cash Flow from Operations Components

**Direct Method:**
- **Cash Receipts from Customers**: Revenue adjustments for A/R changes
- **Cash Payments to Suppliers**: COGS and inventory adjustments
- **Cash Payments to Employees**: Salary and payroll-related payments
- **Cash Payments for Operating Expenses**: SG&A cash outflows
- **Interest and Tax Payments**: Cash outflows for financing costs and taxes

**Indirect Method (Reconciliation):**
- **Starting Point**: Net Income
- **Add Back**: Non-cash expenses (depreciation, amortization, stock compensation)
- **Subtract**: Non-cash income (gains on asset sales, equity income)
- **Adjust for Working Capital Changes**: A/R, inventory, A/P, accruals
- **Result**: Cash Flow from Operations

### Quality of Earnings Assessment

**High-Quality Earnings Indicators:**
- **Cash Conversion**: Operating cash flow > Net income consistently
- **Working Capital Management**: Stable or improving working capital ratios
- **Recurring Income**: High proportion of recurring vs. one-time income
- **Conservative Accounting**: Conservative revenue recognition and expense accruals

**Red Flags for Earnings Quality:**
- **Cash Flow < Net Income**: Persistent negative working capital changes
- **Growing Receivables**: A/R growing faster than sales
- **Inventory Build-up**: Inventory growing faster than COGS
- **One-Time Items**: Frequent "non-recurring" charges
- **Aggressive Accounting**: Channel stuffing, premature revenue recognition

**Cash Conversion Cycle Analysis:**
CCC = DIO + DSO - DPO

Where:
- DIO = Days Inventory Outstanding = (Inventory / COGS) × 365
- DSO = Days Sales Outstanding = (A/R / Revenue) × 365  
- DPO = Days Payable Outstanding = (A/P / COGS) × 365

**Interpretation:**
- **Shorter CCC**: Better working capital efficiency
- **Negative CCC**: Company collects cash before paying suppliers
- **Lengthening CCC**: May indicate operational difficulties
- **Industry Comparison**: CCC varies significantly by industry

## Free Cash Flow Analysis

### Free Cash Flow Calculations

**Free Cash Flow to the Firm (FCFF):**
FCFF = CFO + Interest×(1-Tax Rate) - Capital Expenditures

Or:
FCFF = EBIT×(1-Tax Rate) + Depreciation - CapEx - Δ Working Capital

**Free Cash Flow to Equity (FCFE):**
FCFE = CFO - Capital Expenditures + Net Borrowing

Or:
FCFE = FCFF - Interest×(1-Tax Rate) + Net Borrowing

**Levered vs. Unlevered Free Cash Flow:**
- **FCFF (Unlevered)**: Available to all capital providers
- **FCFE (Levered)**: Available only to equity holders
- **Use in Valuation**: FCFF for enterprise value, FCFE for equity value

### Capital Expenditure Analysis

**Maintenance vs. Growth CapEx:**
- **Maintenance CapEx**: Replace existing assets, maintain current operations
- **Growth CapEx**: Expand operations, enter new markets
- **Estimation**: Maintenance CapEx ≈ Depreciation (rough approximation)
- **Analysis**: Growth CapEx should generate returns > cost of capital

**CapEx Intensity Analysis:**
- **CapEx/Revenue Ratio**: Capital intensity of business model
- **CapEx/Depreciation Ratio**: Investment vs. asset replacement
- **Industry Comparison**: Manufacturing vs. services vs. technology
- **Cyclical Patterns**: CapEx timing and business cycles

**Return on Invested Capital (ROIC):**
ROIC = NOPAT / Invested Capital

Where:
- NOPAT = Net Operating Profit After Tax
- Invested Capital = Working Capital + Fixed Assets + Intangibles

**ROIC Analysis:**
- **Benchmark**: Compare to Weighted Average Cost of Capital (WACC)
- **ROIC > WACC**: Value creation
- **ROIC < WACC**: Value destruction
- **Trend Analysis**: Improving vs. deteriorating returns

## Cash Flow Quality Assessment

### Cash Flow Patterns

**Healthy Cash Flow Profile:**
- **Operating CF**: Consistently positive and growing
- **Investing CF**: Negative (investing in growth)
- **Financing CF**: Varies based on capital needs and strategy
- **Net Cash Flow**: Positive overall or improving trend

**Warning Patterns:**
- **Negative Operating CF**: Operational difficulties
- **Large Positive Investing CF**: Asset liquidation (potential distress)
- **Heavy Financing Dependence**: Unsustainable external financing needs
- **Volatile Patterns**: Unpredictable business model

### Working Capital Analysis

**Working Capital Changes Impact:**
- **Accounts Receivable Increase**: Uses cash (customers paying slower)
- **Inventory Increase**: Uses cash (building stock)
- **Accounts Payable Increase**: Provides cash (paying suppliers slower)
- **Accrued Liabilities Increase**: Provides cash (deferred payments)

**Seasonal Patterns:**
- **Retail Companies**: Holiday season working capital build-up
- **Agricultural**: Seasonal inventory and receivables patterns
- **Construction**: Weather-dependent activity patterns
- **Analysis**: Normalize for seasonal effects

**Working Capital Management Efficiency:**
- **A/R Management**: Collection policies and procedures
- **Inventory Management**: Just-in-time vs. buffer stock strategies
- **A/P Management**: Payment terms negotiation and timing
- **Cash Management**: Optimal cash balances and investment

## Advanced Cash Flow Techniques

### Cash Flow Forecasting

**Pro Forma Cash Flow Statements:**
1. **Operating Activities**: Project from income statement and working capital changes
2. **Investing Activities**: Planned CapEx and asset disposals
3. **Financing Activities**: Debt repayments, dividends, share repurchases
4. **Net Cash Flow**: Sum of all three categories

**Scenario Analysis:**
- **Base Case**: Most likely scenario
- **Optimistic Case**: Better than expected performance
- **Pessimistic Case**: Worse than expected performance
- **Stress Test**: Severe adverse conditions

**Sensitivity Analysis:**
- **Revenue Growth**: Impact of different growth rates
- **Margin Changes**: Operating leverage effects
- **Working Capital**: Changes in efficiency ratios
- **CapEx Levels**: Investment vs. cash generation trade-offs

### Discounted Cash Flow Valuation

**Enterprise Value Calculation:**
Enterprise Value = Σ [FCFFt / (1+WACC)^t] + Terminal Value

**Terminal Value Methods:**
- **Perpetual Growth**: TV = FCFF(final) × (1+g) / (WACC-g)
- **Exit Multiple**: TV = EBITDA(final) × Exit Multiple
- **Comparison**: Sanity check using both methods

**Equity Value Derivation:**
Equity Value = Enterprise Value - Net Debt + Non-Operating Assets

### Cash Flow-Based Ratios

**Cash Flow Coverage Ratios:**
- **Operating CF/Current Liabilities**: Short-term obligation coverage
- **Operating CF/Total Debt**: Debt servicing capacity
- **Free CF/Debt Service**: Coverage of interest and principal payments
- **Cash Flow/CapEx**: Self-funding capacity for investments

**Cash Flow Quality Ratios:**
- **Operating CF/Net Income**: Cash realization of earnings
- **Operating CF/Revenue**: Cash generation efficiency
- **Free CF/Operating CF**: Cash available after investments
- **Cash Conversion**: Working capital efficiency

## Industry-Specific Cash Flow Analysis

### Technology Companies

**R&D Capitalization:**
- **Current Treatment**: R&D expensed immediately
- **Analytical Adjustment**: Capitalize and amortize R&D
- **Impact**: Better matching of investment and benefits
- **Cash Flow**: Adjust for capitalized vs. expensed R&D

**Stock-Based Compensation:**
- **Non-Cash Expense**: Add back to operating cash flow
- **Economic Cost**: Real cost to shareholders (dilution)
- **Cash Tax Benefit**: Deduction for tax purposes
- **Analysis**: Consider both cash and economic impact

### Manufacturing Companies

**Working Capital Intensity:**
- **High Inventory Levels**: Raw materials, WIP, finished goods
- **Seasonal Patterns**: Build-up before peak seasons
- **Supply Chain**: Impact of supplier payment terms
- **Economic Cycles**: Inventory adjustment during downturns

**Capital Intensity:**
- **High CapEx Requirements**: Equipment, facilities
- **Depreciation**: Large non-cash charges
- **Maintenance vs. Growth**: Distinguishing CapEx types
- **Cyclical Investment**: Timing of capacity expansions

### Service Companies

**Low Asset Intensity:**
- **Minimal CapEx**: Primarily IT and office equipment
- **High Cash Conversion**: Low working capital requirements
- **Human Capital**: Key asset not reflected in balance sheet
- **Scalability**: High operating leverage potential

**Subscription Models:**
- **Deferred Revenue**: Cash collected before service delivery
- **Customer Acquisition Costs**: Upfront investment for future cash flows
- **Churn Analysis**: Impact on future cash flow sustainability
- **Lifetime Value**: Long-term cash flow per customer

Cash flow analysis provides crucial insights into a company's financial health, operational efficiency, and ability to generate sustainable returns for stakeholders.`
        },
        {
          id: "cash-flow-forecasting-tool",
          title: "Cash Flow Forecasting Model",
          description: "Build comprehensive cash flow forecasting models with scenario analysis",
          type: "finance-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "cash-flow-modeling",
            instructions: "Create a detailed cash flow forecast model incorporating operating, investing, and financing activities. Test different scenarios and analyze cash flow sustainability.",
            scenario: {
              historicalData: {
                revenue: [800000, 900000, 1000000],
                operatingMargin: [0.15, 0.16, 0.17],
                workingCapitalDays: [45, 47, 44],
                capexAsPercentRevenue: [0.08, 0.09, 0.07]
              },
              forecastAssumptions: {
                revenueGrowth: [0.12, 0.10, 0.08],
                marginImprovement: [0.01, 0.005, 0.003],
                workingCapitalEfficiency: "stable",
                capexPlans: "growth-focused"
              }
            },
            hints: [
              "Start with revenue projections and work down to operating cash flow",
              "Consider working capital changes based on revenue growth",
              "Account for planned capital investments and their timing",
              "Include financing activities like debt repayment and dividends"
            ]
          }
        }
      ],
      quiz: {
        id: "cash-flow-quiz",
        title: "Cash Flow Analysis Quiz",
        description: "Test your understanding of cash flow analysis and quality assessment",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What does it typically mean when operating cash flow is consistently lower than net income?",
            options: [
              "The company is very profitable",
              "There may be earnings quality issues",
              "The company has low depreciation",
              "Working capital is well managed"
            ],
            correctAnswer: 1,
            explanation: "When operating cash flow is consistently lower than net income, it may indicate earnings quality issues such as aggressive revenue recognition or poor working capital management."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "A negative cash conversion cycle typically indicates:",
            options: [
              "Poor working capital management",
              "The company collects cash before paying suppliers",
              "High inventory levels",
              "Slow customer collections"
            ],
            correctAnswer: 1,
            explanation: "A negative cash conversion cycle means the company collects cash from customers before it has to pay suppliers, which is a very favorable working capital position."
          }
        ]
      }
    },

    {
      id: "credit-analysis",
      title: "Credit Analysis Framework",
      description: "Credit risk assessment, default probability, and rating methodologies",
      order: 4,
      estimatedHours: 4.5,
      content: [
        {
          id: "credit-fundamentals",
          title: "Credit Analysis Fundamentals",
          description: "Understanding credit risk and assessment techniques",
          type: "video",
          duration: 40
        },
        {
          id: "credit-risk-assessment",
          title: "Advanced Credit Risk Assessment",
          description: "Comprehensive credit analysis framework and rating methodologies",
          type: "text",
          duration: 55,
          textContent: `# Advanced Credit Risk Assessment

## Credit Analysis Framework

### The Five Cs of Credit

**Character:**
- **Management Quality**: Track record, experience, integrity
- **Corporate Governance**: Board composition, transparency, controls
- **Payment History**: Historical payment patterns and reliability
- **Reputation**: Industry standing and stakeholder relationships
- **Legal Issues**: Litigation, regulatory problems, compliance

**Capacity:**
- **Cash Flow Analysis**: Ability to generate cash for debt service
- **Debt Service Coverage**: EBITDA/Interest, EBIT/Interest ratios
- **Free Cash Flow**: Available cash after operations and investments
- **Stress Testing**: Performance under adverse scenarios
- **Cyclical Analysis**: Performance across business cycles

**Capital:**
- **Equity Cushion**: Shareholders' equity as protection for creditors
- **Leverage Ratios**: Debt/Equity, Debt/Assets, Debt/EBITDA
- **Capital Structure**: Mix of debt and equity financing
- **Subordination**: Priority in capital structure
- **Tangible Net Worth**: Book value minus intangible assets

**Collateral:**
- **Asset Quality**: Marketability and liquidity of assets
- **Asset Coverage**: Tangible assets relative to debt
- **Lien Position**: Security interest and priority
- **Asset Valuation**: Market vs. book value assessment
- **Depreciation**: Asset value stability over time

**Conditions:**
- **Industry Outlook**: Growth prospects, competitive dynamics
- **Economic Environment**: GDP, interest rates, inflation
- **Regulatory Environment**: Changes in laws and regulations
- **Market Conditions**: Supply/demand dynamics, pricing power
- **Technology Disruption**: Impact of technological change

### Quantitative Credit Analysis

**Profitability Ratios:**
- **EBITDA Margin**: Operating profitability before capital costs
- **Interest Coverage**: EBITDA/Interest, EBIT/Interest
- **Return on Assets**: Net Income/Average Total Assets
- **Operating Margin**: Operating Income/Revenue
- **Gross Margin**: Gross Profit/Revenue

**Leverage Ratios:**
- **Debt/EBITDA**: Total debt relative to cash generation
- **Net Debt/EBITDA**: Debt minus cash relative to EBITDA
- **Debt/Equity**: Financial leverage ratio
- **Debt/Total Capitalization**: Debt/(Debt + Equity)
- **Senior Debt/EBITDA**: Priority debt coverage

**Liquidity Ratios:**
- **Current Ratio**: Current Assets/Current Liabilities
- **Quick Ratio**: (Current Assets - Inventory)/Current Liabilities
- **Cash Ratio**: Cash/Current Liabilities
- **Working Capital**: Current Assets - Current Liabilities
- **Cash Conversion Cycle**: DIO + DSO - DPO

**Coverage Ratios:**
- **EBITDA Coverage**: EBITDA/(Interest + Principal)
- **Fixed Charge Coverage**: EBITDA/(Interest + Lease + Principal)
- **Cash Coverage**: Operating Cash Flow/Total Debt Service
- **Asset Coverage**: (Total Assets - Intangibles)/Total Debt

## Credit Rating Methodologies

### Rating Agency Frameworks

**Moody's Rating Scale:**
- **Investment Grade**: Aaa, Aa, A, Baa
- **Speculative Grade**: Ba, B, Caa, Ca, C
- **Modifiers**: 1 (high), 2 (mid), 3 (low) within rating category
- **Default**: D for defaulted obligations

**S&P Rating Scale:**
- **Investment Grade**: AAA, AA, A, BBB
- **Speculative Grade**: BB, B, CCC, CC, C
- **Modifiers**: + (high), - (low) within rating category
- **Default**: D for defaulted obligations

**Rating Factors:**
- **Business Risk**: Industry dynamics, competitive position
- **Financial Risk**: Leverage, coverage, liquidity
- **Management Risk**: Strategy, governance, execution
- **Country Risk**: Sovereign risk, regulatory environment

### Internal Credit Scoring

**Scoring Model Development:**
1. **Variable Selection**: Choose relevant financial and non-financial factors
2. **Weight Assignment**: Determine relative importance of factors
3. **Score Calculation**: Combine weighted factors into overall score
4. **Rating Assignment**: Map scores to internal rating scale
5. **Model Validation**: Test predictive accuracy and calibration

**Common Scoring Variables:**
- **Profitability**: ROA, ROE, EBITDA margin
- **Leverage**: Debt/EBITDA, Debt/Equity
- **Coverage**: Interest coverage, EBITDA coverage
- **Liquidity**: Current ratio, cash/debt
- **Quality**: Earnings quality, audit opinions

**Industry Adjustments:**
- **Sector-Specific Ratios**: Relevant to business model
- **Peer Comparison**: Relative performance metrics
- **Cyclical Adjustments**: Through-the-cycle performance
- **Regulatory Factors**: Industry-specific requirements

## Default Prediction Models

### Altman Z-Score

**Original Model (Public Companies):**
Z = 1.2X1 + 1.4X2 + 3.3X3 + 0.6X4 + 1.0X5

Where:
- X1 = Working Capital/Total Assets
- X2 = Retained Earnings/Total Assets
- X3 = EBIT/Total Assets
- X4 = Market Value Equity/Total Liabilities
- X5 = Sales/Total Assets

**Interpretation:**
- **Z > 2.99**: Safe zone (low default probability)
- **1.81 < Z < 2.99**: Gray zone (moderate risk)
- **Z < 1.81**: Distress zone (high default probability)

**Z-Score Variants:**
- **Private Company Model**: Book value instead of market value
- **Emerging Markets Model**: Adjusted coefficients
- **Service Company Model**: Modified for asset-light businesses

### Merton Distance-to-Default

**Structural Model Approach:**
- **Asset Value Process**: Model firm value as geometric Brownian motion
- **Default Trigger**: Default when asset value < debt value
- **Distance-to-Default**: Number of standard deviations to default
- **Default Probability**: Calculated using Black-Scholes framework

**KMV-Merton Model:**
DD = [ln(VA/X) + (μ - σA²/2)T] / (σA√T)

Where:
- VA = Asset value
- X = Default point (debt level)
- μ = Expected return on assets
- σA = Asset volatility
- T = Time horizon

### Machine Learning Models

**Logistic Regression:**
- **Binary Outcome**: Default vs. non-default
- **Feature Engineering**: Financial ratios, industry factors
- **Probability Output**: Estimated default probability
- **Model Interpretation**: Coefficient significance and direction

**Random Forest:**
- **Ensemble Method**: Multiple decision trees
- **Feature Importance**: Identify key default predictors
- **Handling Non-Linearity**: Capture complex relationships
- **Out-of-Sample Testing**: Cross-validation for robustness

**Neural Networks:**
- **Deep Learning**: Multiple hidden layers
- **Pattern Recognition**: Complex non-linear relationships
- **Large Datasets**: Effective with big data
- **Black Box**: Less interpretable than traditional models

## Stress Testing and Scenario Analysis

### Stress Testing Framework

**Base Case Scenario:**
- **Expected Performance**: Most likely business performance
- **Current Trends**: Continuation of recent performance
- **Management Guidance**: Incorporating forward guidance
- **Industry Outlook**: Consensus industry expectations

**Stress Scenarios:**
- **Mild Stress**: 10-15% revenue decline, margin compression
- **Moderate Stress**: 20-30% revenue decline, significant margin impact
- **Severe Stress**: 40%+ revenue decline, major operational disruption
- **Tail Risk**: Extreme scenarios with very low probability

**Key Variables to Stress:**
- **Revenue**: Organic growth, pricing, volume
- **Costs**: Variable costs, fixed cost leverage
- **Working Capital**: Cash conversion cycle changes
- **Capital Expenditures**: Ability to defer investments
- **Financing**: Access to capital markets

### Scenario Development

**Macroeconomic Scenarios:**
- **Recession**: GDP contraction, rising unemployment
- **Interest Rate Shock**: Significant rate increases
- **Commodity Price Volatility**: Input cost variations
- **Currency Devaluation**: Foreign exchange impacts
- **Regulatory Changes**: New laws or regulations

**Industry-Specific Scenarios:**
- **Technology**: Disruption, obsolescence, competition
- **Retail**: Consumer spending, e-commerce shift
- **Energy**: Commodity prices, environmental regulations
- **Healthcare**: Regulatory approval, reimbursement changes
- **Financial Services**: Credit losses, regulatory capital

**Company-Specific Scenarios:**
- **Key Customer Loss**: Major customer concentration risk
- **Product Recall**: Quality issues, liability costs
- **Cybersecurity Breach**: Data security, reputation risk
- **Management Turnover**: Key person risk
- **Labor Disputes**: Strike, union negotiations

## Credit Enhancement and Mitigation

### Structural Features

**Security and Collateral:**
- **First Lien**: Priority claim on specific assets
- **Second Lien**: Subordinated security interest
- **Unsecured**: General claim against borrower
- **Cross-Collateralization**: Multiple assets securing debt
- **Asset Coverage Tests**: Minimum collateral requirements

**Financial Covenants:**
- **Maintenance Covenants**: Ongoing financial metrics requirements
- **Incurrence Covenants**: Restrictions on additional actions
- **Leverage Tests**: Maximum debt/EBITDA ratios
- **Coverage Tests**: Minimum interest coverage requirements
- **Liquidity Tests**: Minimum cash or credit availability

**Operational Covenants:**
- **Business Restrictions**: Limitations on business changes
- **Investment Limitations**: CapEx and acquisition restrictions
- **Dividend Restrictions**: Limitations on distributions
- **Asset Sale Restrictions**: Approval requirements
- **Merger Restrictions**: Change of control provisions

### Credit Support

**Guarantees:**
- **Corporate Guarantee**: Parent company support
- **Personal Guarantee**: Individual guarantee (small business)
- **Limited Guarantee**: Capped guarantee amount
- **Continuing Guarantee**: Ongoing obligations
- **Payment vs. Collection**: Guarantee structure

**Letters of Credit:**
- **Standby LC**: Credit support mechanism
- **Performance LC**: Contract performance guarantee
- **Commercial LC**: Trade finance tool
- **Confirmed LC**: Bank confirmation added
- **Revolving LC**: Renewable credit support

**Insurance and Hedging:**
- **Credit Default Swaps**: Credit risk transfer
- **Political Risk Insurance**: Country risk mitigation
- **Key Person Insurance**: Management risk coverage
- **Business Interruption**: Operational risk coverage
- **Interest Rate Hedging**: Financial risk management

Credit analysis combines quantitative metrics with qualitative assessment to evaluate the probability of default and potential loss given default, providing the foundation for prudent lending and investment decisions.`
        },
        {
          id: "credit-rating-simulator",
          title: "Credit Rating Assessment Tool",
          description: "Assess credit risk and assign ratings using comprehensive analysis framework",
          type: "finance-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "credit-rating",
            instructions: "Analyze companies using the Five Cs framework and assign appropriate credit ratings. Compare your assessments with actual ratings and understand the key factors driving credit risk.",
            scenario: {
              companies: [
                {
                  name: "Manufacturing Corp",
                  industry: "Industrial Manufacturing",
                  financials: {
                    revenue: 2500000,
                    ebitda: 400000,
                    interestExpense: 50000,
                    totalDebt: 800000,
                    cash: 200000,
                    totalAssets: 2000000,
                    currentRatio: 1.8
                  },
                  qualitativeFactors: {
                    managementQuality: "strong",
                    industryOutlook: "stable",
                    competitivePosition: "leading",
                    governanceRating: "good"
                  }
                }
              ],
              ratingCriteria: {
                debtToEbitda: { aaa: "<1.5", aa: "1.5-2.5", a: "2.5-3.5", bbb: "3.5-4.5", bb: ">4.5" },
                interestCoverage: { aaa: ">12", aa: "8-12", a: "5-8", bbb: "3-5", bb: "<3" },
                currentRatio: { strong: ">2.0", adequate: "1.5-2.0", weak: "<1.5" }
              }
            },
            hints: [
              "Calculate key credit ratios and compare to benchmarks",
              "Consider both quantitative metrics and qualitative factors",
              "Assess business risk and financial risk separately",
              "Think about through-the-cycle performance"
            ]
          }
        }
      ],
      quiz: {
        id: "credit-analysis-quiz",
        title: "Credit Analysis Quiz",
        description: "Test your understanding of credit risk assessment and rating methodologies",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which of the Five Cs focuses on management quality and corporate governance?",
            options: [
              "Capacity",
              "Character", 
              "Capital",
              "Conditions"
            ],
            correctAnswer: 1,
            explanation: "Character focuses on the quality and integrity of management, corporate governance, and the company's reputation and track record."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What does a Debt/EBITDA ratio of 6.0x typically indicate?",
            options: [
              "Strong credit profile",
              "Investment grade rating",
              "High leverage and credit risk",
              "Optimal capital structure"
            ],
            correctAnswer: 2,
            explanation: "A Debt/EBITDA ratio of 6.0x indicates high leverage and significant credit risk, typically associated with speculative grade ratings."
          }
        ]
      }
    },

    {
      id: "industry-analysis",
      title: "Industry and Competitive Analysis",
      description: "Sector analysis, competitive positioning, and peer comparison techniques",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "industry-frameworks",
          title: "Industry Analysis Frameworks",
          description: "Porter's Five Forces and industry life cycle analysis",
          type: "video",
          duration: 30
        },
        {
          id: "competitive-analysis",
          title: "Comprehensive Competitive Analysis",
          description: "Industry dynamics, competitive positioning, and peer comparison methodologies",
          type: "text",
          duration: 45,
          textContent: `# Comprehensive Competitive Analysis

## Industry Analysis Framework

### Porter's Five Forces Analysis

**Threat of New Entrants:**
- **Barriers to Entry**: Capital requirements, economies of scale, brand loyalty
- **Regulatory Barriers**: Licensing, permits, safety requirements
- **Access to Distribution**: Control of distribution channels
- **Switching Costs**: Customer cost of changing suppliers
- **Network Effects**: Value increases with user adoption

**Bargaining Power of Suppliers:**
- **Supplier Concentration**: Number and size of suppliers
- **Switching Costs**: Cost of changing suppliers
- **Input Importance**: Criticality of supplier's product
- **Forward Integration**: Supplier's ability to integrate forward
- **Substitute Inputs**: Availability of alternative inputs

**Bargaining Power of Buyers:**
- **Buyer Concentration**: Customer concentration and size
- **Price Sensitivity**: Elasticity of demand
- **Product Differentiation**: Uniqueness of product/service
- **Backward Integration**: Buyer's ability to integrate backward
- **Information Access**: Buyer's knowledge of alternatives

**Threat of Substitutes:**
- **Performance**: Substitute product performance
- **Switching Costs**: Cost of switching to substitutes
- **Price-Performance Trade-off**: Relative value proposition
- **Technology**: Disruptive technologies and innovation
- **Trends**: Consumer preference changes

**Competitive Rivalry:**
- **Number of Competitors**: Market concentration
- **Growth Rate**: Industry growth vs. maturity
- **Fixed Costs**: High fixed costs increase rivalry
- **Product Differentiation**: Commodity vs. differentiated products
- **Exit Barriers**: Difficulty of leaving industry

### Industry Life Cycle Analysis

**Introduction Stage:**
- **Characteristics**: New technology, high costs, limited adoption
- **Financial Metrics**: Negative cash flow, high R&D spending
- **Key Success Factors**: Innovation, patent protection, early mover advantage
- **Investment Considerations**: High risk, potential high returns

**Growth Stage:**
- **Characteristics**: Rapid adoption, expanding market, new entrants
- **Financial Metrics**: Revenue growth, improving margins, positive cash flow
- **Key Success Factors**: Scaling operations, market share capture
- **Investment Considerations**: High growth potential, competitive pressures

**Maturity Stage:**
- **Characteristics**: Stable demand, established competitors, price competition
- **Financial Metrics**: Stable margins, consistent cash generation
- **Key Success Factors**: Cost efficiency, customer retention, differentiation
- **Investment Considerations**: Steady returns, limited growth

**Decline Stage:**
- **Characteristics**: Decreasing demand, industry consolidation, exit of players
- **Financial Metrics**: Declining revenues, margin pressure, capital preservation
- **Key Success Factors**: Cost management, niche positioning, exit planning
- **Investment Considerations**: Value traps, potential turnaround opportunities

## Peer Comparison Analysis

### Peer Group Selection

**Primary Peers:**
- **Same Industry**: Direct competitors with similar business models
- **Similar Size**: Revenue, market cap, geographic scope
- **Comparable Operations**: Products, services, target markets
- **Financial Profile**: Capital structure, profitability levels

**Secondary Peers:**
- **Adjacent Industries**: Related but not identical businesses
- **Similar Business Models**: Different industries but comparable operations
- **Aspirational Peers**: Targets for comparison and benchmarking
- **Historical Peers**: Companies that previously competed directly

**Peer Selection Criteria:**
- **Revenue Size**: Within 50-200% range typically
- **Geographic Overlap**: Similar markets and regions
- **Customer Base**: B2B vs. B2C, customer concentration
- **Regulatory Environment**: Similar regulatory constraints

### Comparative Analysis Metrics

**Profitability Comparison:**
- **Gross Margin**: Product/service profitability
- **Operating Margin**: Operational efficiency
- **Net Margin**: Overall profitability after all costs
- **Return on Assets**: Asset utilization efficiency
- **Return on Equity**: Shareholder return generation

**Efficiency Comparison:**
- **Asset Turnover**: Revenue per dollar of assets
- **Inventory Turnover**: Inventory management efficiency
- **Receivables Turnover**: Collection efficiency
- **Payables Turnover**: Supplier payment management
- **Employee Productivity**: Revenue or profit per employee

**Financial Strength Comparison:**
- **Leverage Ratios**: Debt/equity, debt/EBITDA
- **Coverage Ratios**: Interest coverage, debt service coverage
- **Liquidity Ratios**: Current ratio, quick ratio
- **Credit Ratings**: External credit assessment
- **Cost of Capital**: Borrowing costs and equity cost

### Valuation Multiples Analysis

**Trading Multiples:**
- **P/E Ratio**: Price-to-earnings comparison
- **EV/EBITDA**: Enterprise value to EBITDA
- **P/B Ratio**: Price-to-book value
- **EV/Revenue**: Enterprise value to sales
- **Industry-Specific**: Relevant sector multiples

**Multiple Analysis Process:**
1. **Calculate Multiples**: For target company and peers
2. **Adjust for Differences**: Size, growth, profitability differences
3. **Statistical Analysis**: Mean, median, quartile analysis
4. **Apply to Target**: Estimate valuation range
5. **Sanity Check**: Validate against fundamentals

**Growth-Adjusted Multiples:**
- **PEG Ratio**: P/E ratio divided by growth rate
- **EV/EBITDA/Growth**: Enterprise multiple adjusted for growth
- **Revenue Multiple/Growth**: Sales multiple per unit of growth
- **Interpretation**: Lower ratios suggest better value

## Industry-Specific Analysis

### Technology Sector Analysis

**Key Metrics:**
- **R&D Intensity**: R&D spending as % of revenue
- **Patent Portfolio**: Number and quality of patents
- **Product Development Cycle**: Time to market for new products
- **Customer Acquisition Cost**: Cost to acquire new customers
- **Churn Rate**: Customer retention and loyalty

**Competitive Factors:**
- **Innovation Speed**: Rate of technological advancement
- **Platform Effects**: Network externalities and ecosystems
- **Data Assets**: Proprietary data and analytics capabilities
- **Talent Acquisition**: Ability to attract top technical talent
- **Partnership Ecosystem**: Strategic alliances and integrations

### Retail Sector Analysis

**Key Metrics:**
- **Same-Store Sales Growth**: Organic growth measurement
- **Inventory Turnover**: Merchandise management efficiency
- **Sales per Square Foot**: Space utilization efficiency
- **Gross Margin Trends**: Pricing power and cost management
- **E-commerce Penetration**: Online vs. offline sales mix

**Competitive Factors:**
- **Location Quality**: Prime real estate and foot traffic
- **Brand Strength**: Customer recognition and loyalty
- **Supply Chain**: Inventory management and logistics
- **Omnichannel Integration**: Online-offline coordination
- **Private Label**: Exclusive product differentiation

### Manufacturing Sector Analysis

**Key Metrics:**
- **Capacity Utilization**: Production efficiency measurement
- **Unit Costs**: Cost per unit of production
- **Quality Metrics**: Defect rates, returns, warranties
- **Working Capital Intensity**: Investment in operations
- **CapEx Intensity**: Investment in productive capacity

**Competitive Factors:**
- **Cost Position**: Relative cost structure advantages
- **Product Quality**: Performance and reliability
- **Manufacturing Flexibility**: Ability to adapt production
- **Supply Chain**: Supplier relationships and logistics
- **Automation Level**: Technology and efficiency

### Financial Services Analysis

**Key Metrics:**
- **Return on Assets**: Asset utilization efficiency
- **Net Interest Margin**: Spread between borrowing and lending
- **Efficiency Ratio**: Operating expenses/revenue
- **Loan Loss Provisions**: Credit risk management
- **Tier 1 Capital Ratio**: Regulatory capital strength

**Competitive Factors:**
- **Distribution Network**: Branch network and digital presence
- **Product Range**: Breadth of financial services
- **Risk Management**: Credit and operational risk controls
- **Regulatory Compliance**: Adherence to banking regulations
- **Technology Platform**: Digital transformation progress

## Strategic Position Assessment

### Competitive Advantages

**Cost Advantages:**
- **Scale Economies**: Lower unit costs through size
- **Experience Curve**: Learning and efficiency improvements
- **Resource Access**: Preferential access to inputs
- **Process Innovation**: Proprietary efficient processes
- **Location Benefits**: Geographic cost advantages

**Differentiation Advantages:**
- **Product Innovation**: Unique features and capabilities
- **Brand Strength**: Customer recognition and loyalty
- **Service Quality**: Superior customer experience
- **Distribution Access**: Unique channels to market
- **Customer Relationships**: Switching costs and loyalty

**Sustainability of Advantages:**
- **Imitability**: Difficulty for competitors to copy
- **Durability**: How long advantage can be maintained
- **Appropriability**: Company's ability to capture value
- **Complementary Assets**: Supporting capabilities
- **Dynamic Capabilities**: Ability to adapt and innovate

### Market Share Analysis

**Market Definition:**
- **Product Market**: Specific products and services
- **Geographic Market**: Relevant geographic boundaries
- **Customer Segments**: Target customer groups
- **Distribution Channels**: Relevant sales channels

**Share Measurement:**
- **Revenue Share**: Percentage of total market revenue
- **Unit Share**: Percentage of total units sold
- **Customer Share**: Percentage of total customers
- **Wallet Share**: Percentage of customer spending

**Share Analysis:**
- **Market Leadership**: Position relative to competitors
- **Share Trends**: Growing, stable, or declining position
- **Share Drivers**: Factors influencing market position
- **Share Defense**: Competitive responses and protection

## Scenario Analysis and Forecasting

### Industry Scenario Development

**Base Case Scenario:**
- **Current Trends**: Continuation of existing patterns
- **Consensus Forecasts**: Industry analyst expectations
- **Company Guidance**: Management outlook and guidance
- **Economic Assumptions**: GDP, interest rates, inflation

**Optimistic Scenario:**
- **Accelerated Growth**: Above-trend industry expansion
- **Market Share Gains**: Competitive position improvement
- **Operational Excellence**: Margin expansion and efficiency
- **Innovation Success**: New product/service success

**Pessimistic Scenario:**
- **Market Contraction**: Industry decline or disruption
- **Competitive Pressure**: Market share loss, pricing pressure
- **Operational Challenges**: Cost increases, efficiency loss
- **External Shocks**: Economic recession, regulatory changes

### Competitive Response Analysis

**Competitive Actions:**
- **Pricing Strategies**: Price changes and competitive responses
- **Product Launches**: New product introductions and timing
- **Capacity Additions**: Industry capacity changes
- **M&A Activity**: Consolidation and market structure changes

**Response Modeling:**
- **Game Theory**: Strategic interaction modeling
- **Nash Equilibrium**: Stable competitive outcomes
- **First-Mover Advantage**: Benefits of early action
- **Competitive Intelligence**: Monitoring competitor actions

Industry and competitive analysis provides the context for understanding a company's financial performance and future prospects within its competitive environment.`
        }
      ],
      quiz: {
        id: "industry-analysis-quiz",
        title: "Industry and Competitive Analysis Quiz",
        description: "Test your understanding of industry analysis and competitive positioning",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which of Porter's Five Forces focuses on the ability of customers to drive down prices?",
            options: [
              "Threat of new entrants",
              "Bargaining power of suppliers",
              "Bargaining power of buyers",
              "Threat of substitutes"
            ],
            correctAnswer: 2,
            explanation: "The bargaining power of buyers focuses on customers' ability to influence pricing and terms, including their ability to drive down prices."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In which industry life cycle stage is competitive rivalry typically highest?",
            options: [
              "Introduction",
              "Growth",
              "Maturity",
              "Decline"
            ],
            correctAnswer: 2,
            explanation: "Competitive rivalry is typically highest in the maturity stage when growth slows, markets become saturated, and companies compete intensely for market share."
          }
        ]
      }
    },

    {
      id: "advanced-topics",
      title: "Advanced Financial Analysis Topics",
      description: "Complex transactions, off-balance sheet items, and international considerations",
      order: 6,
      estimatedHours: 4.5,
      content: [
        {
          id: "complex-transactions",
          title: "Complex Transactions and Structures",
          description: "M&A accounting, joint ventures, and special purpose entities",
          type: "video",
          duration: 40
        },
        {
          id: "international-analysis",
          title: "International Financial Statement Analysis",
          description: "Currency translation, IFRS vs GAAP, and cross-border analysis challenges",
          type: "text",
          duration: 50,
          textContent: `# International Financial Statement Analysis

## Accounting Standards Differences

### IFRS vs. US GAAP Comparison

**Revenue Recognition:**
- **IFRS 15 vs. ASC 606**: Both use five-step model but implementation details differ
- **Construction Contracts**: IFRS allows percentage-of-completion for all contracts
- **Multiple Element Arrangements**: Different allocation approaches
- **Timing Differences**: IFRS may recognize revenue earlier in some cases

**Asset Valuation:**
- **Property, Plant & Equipment**: IFRS allows revaluation model, US GAAP requires historical cost
- **Intangible Assets**: IFRS permits revaluation for active markets
- **Investment Property**: IFRS allows fair value model
- **Inventory**: IFRS prohibits LIFO method, US GAAP allows it

**Financial Instruments:**
- **Classification**: IFRS 9 vs. ASC 326 for expected credit losses
- **Hedge Accounting**: IFRS more principles-based approach
- **Fair Value Options**: Different scope and application
- **Embedded Derivatives**: Different separation requirements

**Consolidation:**
- **Control Definition**: IFRS focuses on power and variable returns
- **Special Purpose Entities**: Different consolidation criteria
- **Joint Arrangements**: IFRS 11 vs. US GAAP treatment
- **Non-controlling Interests**: Different measurement approaches

### Currency Translation Methods

**Current Rate Method:**
- **When Used**: Functional currency differs from reporting currency
- **Translation Rates**: Current rate for all items except equity
- **Translation Gains/Losses**: Recorded in other comprehensive income
- **Net Investment**: Hedging of net investment in foreign operations

**Temporal Method:**
- **When Used**: Functional currency same as reporting currency
- **Translation Rates**: Historical rates for non-monetary items
- **Translation Gains/Losses**: Recorded in income statement
- **Monetary vs. Non-Monetary**: Different treatment for each category

**Translation Impact Analysis:**
- **Income Statement**: Revenue and expenses translated at average rate
- **Balance Sheet**: Assets and liabilities at current rate
- **Equity Impact**: Translation adjustments in accumulated OCI
- **Cash Flow**: Operating flows at average rate, others at transaction date rates

## Cross-Border Analysis Challenges

### Economic Environment Factors

**Inflation Impact:**
- **Hyperinflationary Economies**: IAS 29 adjustments for price changes
- **Real vs. Nominal**: Adjusting for inflation effects
- **Purchasing Power**: Currency purchasing power changes
- **Historical Cost Distortions**: Asset values in inflationary environments

**Interest Rate Environment:**
- **Real Interest Rates**: Nominal rates adjusted for inflation
- **Term Structure**: Yield curve differences across countries
- **Central Bank Policy**: Monetary policy impact on businesses
- **Credit Risk Premiums**: Country risk reflected in borrowing costs

**Regulatory Environment:**
- **Capital Controls**: Restrictions on capital movements
- **Foreign Exchange Controls**: Limits on currency conversion
- **Transfer Pricing Rules**: International tax considerations
- **Local GAAP**: Statutory reporting requirements

### Country Risk Assessment

**Political Risk:**
- **Government Stability**: Political system and leadership changes
- **Policy Continuity**: Consistency of economic policies
- **Expropriation Risk**: Nationalization or seizure of assets
- **Civil Unrest**: Social stability and security concerns

**Economic Risk:**
- **GDP Growth**: Economic growth trends and stability
- **Inflation Rates**: Price stability and monetary policy
- **Current Account**: Balance of payments sustainability
- **External Debt**: Foreign debt levels and servicing capacity

**Financial Risk:**
- **Currency Stability**: Exchange rate volatility and trends
- **Banking System**: Financial system stability and development
- **Capital Markets**: Market depth and liquidity
- **Foreign Reserves**: Adequacy of foreign exchange reserves

### Analytical Adjustments

**Normalization Adjustments:**
- **One-Time Items**: Remove non-recurring gains/losses
- **Accounting Policy**: Standardize accounting methods
- **Currency Effects**: Adjust for translation impacts
- **Hyperinflation**: Price-level adjusted figures

**Comparability Enhancements:**
- **Same Accounting Standards**: Convert to common accounting basis
- **Same Currency**: Translate to common currency
- **Same Time Period**: Align fiscal year ends
- **Same Business Mix**: Adjust for different business segments

## Emerging Market Considerations

### Financial Reporting Quality

**Disclosure Standards:**
- **Transparency Levels**: Varying disclosure requirements
- **Related Party Transactions**: Family/government connections
- **Segment Reporting**: Geographic and business segment detail
- **Risk Disclosures**: Market, credit, and operational risk information

**Audit Quality:**
- **Auditor Selection**: International vs. local audit firms
- **Audit Standards**: ISA vs. local auditing standards
- **Independence**: Auditor independence and rotation requirements
- **Opinion Quality**: Modified opinions and going concern issues

**Corporate Governance:**
- **Board Composition**: Independent directors and oversight
- **Minority Rights**: Protection of minority shareholders
- **Ownership Structure**: Concentrated vs. dispersed ownership
- **Management Compensation**: Alignment with shareholder interests

### Market Structure Factors

**Capital Market Development:**
- **Market Capitalization**: Size relative to GDP
- **Trading Volume**: Liquidity and price discovery
- **Foreign Participation**: International investor access
- **Market Infrastructure**: Trading systems and settlement

**Banking System:**
- **Financial Intermediation**: Bank vs. capital market financing
- **Foreign Bank Presence**: International banking participation
- **Credit Availability**: Access to financing for businesses
- **Interest Rate Structure**: Lending and deposit rates

**Institutional Framework:**
- **Legal System**: Commercial law and enforcement
- **Property Rights**: Protection of intellectual and physical property
- **Contract Enforcement**: Judicial system effectiveness
- **Regulatory Oversight**: Securities regulation and enforcement

## Practical Analysis Framework

### Multi-Country Comparison Process

**Step 1: Standardization**
- Convert all figures to common currency (USD typically)
- Adjust for different accounting standards
- Normalize for different fiscal year ends
- Remove one-time and extraordinary items

**Step 2: Economic Context**
- Analyze macroeconomic environment for each country
- Consider inflation, interest rates, and exchange rates
- Assess country risk ratings and sovereign credit quality
- Understand regulatory and competitive environment

**Step 3: Company Analysis**
- Calculate financial ratios using standardized figures
- Compare ratios to local and international peers
- Analyze trends over multiple years
- Consider business model and competitive position

**Step 4: Risk Assessment**
- Evaluate country-specific risks
- Assess currency risk and hedging strategies
- Consider political and regulatory risks
- Analyze concentration risks by geography

**Step 5: Valuation Adjustments**
- Apply country risk premiums to discount rates
- Adjust multiples for market development differences
- Consider liquidity discounts for emerging markets
- Factor in currency and political risk premiums

### Key Performance Indicators

**Profitability Metrics:**
- **Local Currency**: Growth in local currency terms
- **Constant Currency**: Organic growth excluding FX effects
- **Purchasing Power**: Real returns adjusted for inflation
- **Relative Performance**: Performance vs. local market indices

**Efficiency Ratios:**
- **Asset Utilization**: Adjusted for asset revaluation differences
- **Working Capital**: Considering local payment practices
- **Capital Intensity**: Industry norms by country
- **Labor Productivity**: Adjusted for wage and skill differences

**Financial Strength:**
- **Local Funding**: Dependence on local vs. international financing
- **Currency Matching**: Assets vs. liabilities currency alignment
- **Covenant Compliance**: Local vs. international debt agreements
- **Liquidity Access**: Local banking relationships and credit facilities

International financial analysis requires deep understanding of local accounting standards, economic conditions, and market structures to make meaningful cross-border comparisons and investment decisions.`
        }
      ],
      quiz: {
        id: "advanced-topics-quiz",
        title: "Advanced Financial Analysis Quiz",
        description: "Test your understanding of complex financial analysis topics",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Under IFRS, where are foreign currency translation adjustments typically recorded?",
            options: [
              "Income statement as other income",
              "Other comprehensive income",
              "Retained earnings directly",
              "As a separate component of liabilities"
            ],
            correctAnswer: 1,
            explanation: "Under IFRS, foreign currency translation adjustments are typically recorded in other comprehensive income as part of the cumulative translation adjustment."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the primary challenge when comparing companies across different accounting standards?",
            options: [
              "Different currencies",
              "Different time zones",
              "Different recognition and measurement principles",
              "Different management teams"
            ],
            correctAnswer: 2,
            explanation: "The primary challenge is different recognition and measurement principles between accounting standards, which can significantly impact reported financial results and ratios."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "company-analysis-project",
      title: "Complete Company Analysis Project", 
      description: "Conduct comprehensive financial statement analysis of a public company including ratio analysis, peer comparison, and investment recommendation",
      type: "project",
      dueWeeks: 6,
      maxScore: 100,
      instructions: `# Company Analysis Project

## Objective
Perform comprehensive financial statement analysis of a public company and provide investment recommendation based on financial health and performance.

## Requirements
1. **Company Selection**: Choose S&P 500 company with 5+ years of data
2. **Financial Statement Analysis**: Analyze trends in all three statements
3. **Ratio Analysis**: Calculate comprehensive ratios across all categories
4. **Peer Comparison**: Compare to 3-4 industry competitors
5. **Credit Assessment**: Evaluate creditworthiness and default risk
6. **Investment Thesis**: Buy/hold/sell recommendation with target price

## Deliverables
- Executive summary with investment recommendation
- Detailed analysis report (18-22 pages)
- Excel model with calculations and forecasts
- Peer comparison dashboard
- Risk assessment and scenario analysis

## Evaluation Criteria
- Analytical depth and accuracy (35%)
- Financial modeling quality (25%)
- Investment insights and reasoning (25%)
- Communication and presentation (15%)`,
      submissionFormat: "pdf",
      resources: [
        "SEC filing database access",
        "Industry reports and peer data",
        "Financial analysis templates"
      ]
    }
  ],

  finalExam: {
    id: "financial-analysis-final",
    title: "Financial Statement Analysis Final Exam",
    description: "Comprehensive examination of financial statement analysis techniques and applications",
    timeLimit: 120,
    passingScore: 80,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "What does a high accounts receivable turnover ratio indicate?",
        options: [
          "Poor collection policies",
          "Efficient collection of receivables",
          "High credit sales volume", 
          "Aggressive accounting practices"
        ],
        correctAnswer: 1,
        explanation: "A high accounts receivable turnover ratio indicates efficient collection of receivables, as it shows the company collects its outstanding accounts receivable frequently during the period."
      },
      {
        id: "final-q2",
        type: "multiple-choice", 
        question: "Which cash flow component is most important for assessing long-term financial health?",
        options: [
          "Net income",
          "Operating cash flow",
          "Investing cash flow",
          "Financing cash flow"
        ],
        correctAnswer: 1,
        explanation: "Operating cash flow is most important for long-term financial health as it shows the company's ability to generate cash from its core business operations."
      }
    ]
  },

  quizzes: [
    {
      id: "comprehensive-analysis-quiz",
      title: "Comprehensive Financial Analysis Quiz",
      description: "Advanced quiz covering all aspects of financial statement analysis",
      timeLimit: 30,
      passingScore: 85,
      attempts: 2,
      questions: [
        {
          id: "comp-q1",
          type: "multiple-choice",
          question: "A company with increasing revenue but decreasing operating cash flow might indicate:",
          options: [
            "Strong business performance",
            "Potential earnings quality issues",
            "Excellent working capital management",
            "Conservative accounting practices"
          ],
          correctAnswer: 1,
          explanation: "Increasing revenue with decreasing operating cash flow may indicate earnings quality issues such as aggressive revenue recognition or deteriorating working capital management."
        },
        {
          id: "comp-q2",
          type: "multiple-choice",
          question: "In credit analysis, which ratio is most important for assessing debt servicing ability?",
          options: [
            "Current ratio",
            "Debt-to-equity ratio",
            "Interest coverage ratio",
            "Asset turnover ratio"
          ],
          correctAnswer: 2,
          explanation: "Interest coverage ratio (EBITDA/Interest or EBIT/Interest) is most important for assessing a company's ability to service its debt obligations."
        },
        {
          id: "comp-q3",
          type: "multiple-choice",
          question: "When analyzing a company in a mature industry, which factor is typically most critical?",
          options: [
            "Revenue growth potential",
            "Cost management and efficiency",
            "New product development",
            "Market expansion opportunities"
          ],
          correctAnswer: 1,
          explanation: "In mature industries with limited growth, cost management and operational efficiency become the primary drivers of profitability and competitive advantage."
        }
      ]
    }
  ]
};